import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useVentanaApilada } from '../components/Modal';
import { usePreferenciasAyuda } from './usePreferenciasAyuda';
import { buscarObjetivo, esCampo, indicePaso, opcionesDe, pasoVigente, resolverPaso, textoDe } from './motor';
import type { AccionesPagina, EstadoGuia, Guia, OpcionPregunta, Respuestas } from './tipos';

interface GuiaInteractivaProps<C> {
  guia: Guia<C>;
  /** El formulario está abierto (p. ej. la ventana "Nuevo remito"). Al cerrarlo la guía se reinicia. */
  activa: boolean;
  /**
   * Oculta la guía sin perder dónde iba (p. ej. mientras la ventana pregunta si descartar los cambios): no se dibuja ni
   * atiende Escape, y al terminar la pausa sigue en el mismo paso, con las respuestas dadas y, si se saltó, cerrada.
   */
  pausada?: boolean;
  /** Lo que las condiciones y los textos de la guía necesitan saber del formulario. */
  contexto: C;
  acciones?: AccionesPagina;
}

/**
 * Ayuda interactiva sobre un formulario: resalta el campo que toca con una burbuja que explica qué va ahí, o hace una
 * pregunta. Con la ayuda apagada (preferencia del usuario) o la guía oculta no se dibuja nada ni se toca el formulario.
 * Va fuera de la ventana (hermano de `<Modal>`): así entra a la pila de Escape después que ella.
 */
export function GuiaInteractiva<C>(props: GuiaInteractivaProps<C>) {
  const preferencias = usePreferenciasAyuda();
  if (!props.activa || !preferencias.activa || preferencias.ocultas.includes(props.guia.id)) return null;
  return <GuiaEnCurso {...props} ocultar={() => preferencias.ocultarGuia(props.guia.id)} />;
}

interface Rect { top: number; left: number; width: number; height: number }
const mismoRect = (a: Rect | null, b: Rect | null) =>
  a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);
const medir = (el: HTMLElement | null): Rect | null => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const MARGEN = 16;
const SEPARACION = 8;
const ANCHO_MAX = 320;
// Los botones de la burbuja no se llevan el foco del campo: si no, su blur dispararía un change y la guía avanzaría dos veces.
const sinRobarFoco = (e: React.MouseEvent) => e.preventDefault();

function GuiaEnCurso<C>({ guia, contexto, acciones, pausada = false, ocultar }: GuiaInteractivaProps<C> & { ocultar: () => void }) {
  const total = guia.pasos.length;
  // pos: paso que se muestra (null hasta la primera resolución; total = terminó). resolverDesde: pedido de buscar el
  // próximo paso pendiente desde ese índice; se atiende después del render, con el DOM y el contexto al día.
  const [pos, setPos] = useState<number | null>(null);
  const [respuestas, setRespuestas] = useState<Respuestas>({});
  const [resolverDesde, setResolverDesde] = useState<number | null>(0);
  const [cerrada, setCerrada] = useState(false);

  const estado = useRef<EstadoGuia<C>>({ contexto, respuestas });
  estado.current = { contexto, respuestas };
  const accionesRef = useRef(acciones);
  accionesRef.current = acciones;
  const posRef = useRef(pos);
  posRef.current = pos;
  const enfocarLuego = useRef<string | null>(null);

  useEffect(() => {
    if (resolverDesde === null) return;
    setPos(resolverPaso(guia, resolverDesde, estado.current));
    setResolverDesde(null);
  }, [resolverDesde, guia]);

  const visible = !cerrada && !pausada && pos !== null && pos < total;
  const paso = visible ? guia.pasos[pos] : null;

  // Si el paso dejó de corresponder (cambió el contexto) o su campo ya no está en pantalla, se pasa al siguiente.
  useEffect(() => {
    if (!paso || resolverDesde !== null) return;
    if (!pasoVigente(paso, estado.current)) setResolverDesde(pos);
  });

  // Avance solo: cuando se confirma un valor (change) o a un campo le deja de faltar el valor (data-falta), se vuelve a
  // resolver desde el paso actual; si quedó completo, se pasa al siguiente. Mientras se escribe no se avanza.
  useEffect(() => {
    if (!visible) return;
    const reevaluar = () => { if (posRef.current !== null) setResolverDesde(posRef.current); };
    // En burbujeo sobre document: corre después del onChange de React, con el formulario ya actualizado.
    document.addEventListener('change', reevaluar);
    const observador = new MutationObserver(reevaluar);
    observador.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['data-falta'] });
    return () => { document.removeEventListener('change', reevaluar); observador.disconnect(); };
  }, [visible]);

  // Escape cierra la burbuja si es lo de arriba de la pila (no la ventana de abajo).
  const esLoDeArriba = useVentanaApilada(visible);
  useEffect(() => {
    if (!visible) return;
    const alEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !esLoDeArriba()) return;
      // Ninguna otra escucha de Escape actúa con esta tecla (la ventana podría verse arriba si la pila ya se actualizó).
      e.stopImmediatePropagation();
      setCerrada(true);
    };
    document.addEventListener('keydown', alEscape);
    return () => document.removeEventListener('keydown', alEscape);
  }, [visible, esLoDeArriba]);

  // Al llegar a un paso: el campo se muestra y recibe el foco (una pregunta enfoca su primera opción).
  const primeraOpcion = useRef<HTMLButtonElement>(null);
  const clavePaso = visible ? pos : null;
  useEffect(() => {
    if (clavePaso === null) return;
    const actual = guia.pasos[clavePaso];
    if (enfocarLuego.current) {
      const pedido = buscarObjetivo(enfocarLuego.current);
      enfocarLuego.current = null;
      if (pedido) { pedido.focus(); return; }
    }
    if (actual.tipo === 'pregunta') { primeraOpcion.current?.focus(); return; }
    const el = buscarObjetivo(actual.objetivo);
    if (!el) return;
    el.scrollIntoView?.({ block: 'nearest' });
    if (esCampo(el) && document.activeElement !== el) el.focus();
  }, [clavePaso, guia]);

  // Posición del resaltado: se mide en cada render y al desplazar o cambiar el tamaño de la pantalla.
  const [rect, setRect] = useState<Rect | null>(null);
  const objetivo = paso?.tipo === 'campo' ? paso.objetivo : null;
  useLayoutEffect(() => {
    const nuevo = objetivo ? medir(buscarObjetivo(objetivo)) : null;
    setRect(prev => (mismoRect(prev, nuevo) ? prev : nuevo));
  });
  useEffect(() => {
    if (!objetivo) return;
    let cuadro = 0;
    const remedir = () => {
      cancelAnimationFrame(cuadro);
      cuadro = requestAnimationFrame(() => {
        const nuevo = medir(buscarObjetivo(objetivo));
        setRect(prev => (mismoRect(prev, nuevo) ? prev : nuevo));
      });
    };
    window.addEventListener('scroll', remedir, true);
    window.addEventListener('resize', remedir);
    return () => { cancelAnimationFrame(cuadro); window.removeEventListener('scroll', remedir, true); window.removeEventListener('resize', remedir); };
  }, [objetivo]);

  const burbuja = useRef<HTMLDivElement>(null);
  const [altoBurbuja, setAltoBurbuja] = useState(140);
  useLayoutEffect(() => {
    const alto = burbuja.current?.offsetHeight;
    if (alto && alto !== altoBurbuja) setAltoBurbuja(alto);
  });

  const idPregunta = useId();
  const idDetalle = useId();

  if (!paso) return null;

  const siguiente = () => setResolverDesde((pos ?? 0) + 1);
  const saltar = () => setCerrada(true);
  const noMostrarMas = () => { setCerrada(true); ocultar(); };

  const responder = (opcion: OpcionPregunta) => {
    let destino = (pos ?? 0) + 1;
    for (const accion of opcion.acciones ?? []) {
      if (accion.tipo === 'asignar') accionesRef.current?.asignar?.(accion.campo, accion.valor);
      else if (accion.tipo === 'abrir') accionesRef.current?.abrir?.(accion.dialogo);
      else if (accion.tipo === 'enfocar') enfocarLuego.current = accion.objetivo;
      else if (accion.tipo === 'ir') { const i = indicePaso(guia, accion.paso); if (i >= 0) destino = i; }
    }
    setRespuestas(r => ({ ...r, [paso.id]: opcion.valor }));
    setResolverDesde(destino);
  };

  const pie = (
    <button type="button" onMouseDown={sinRobarFoco} onClick={noMostrarMas} className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2">
      No mostrar más en este formulario
    </button>
  );
  const botonSaltar = (
    <button type="button" onMouseDown={sinRobarFoco} onClick={saltar} className="px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 min-h-[2.5rem]">
      Saltar
    </button>
  );

  if (paso.tipo === 'pregunta') {
    const opciones = opcionesDe(paso, estado.current);
    return createPortal(
      <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-slate-900/20">
        <div role="dialog" aria-modal="false" aria-labelledby={idPregunta} className="w-full max-w-sm bg-white rounded-xl shadow-xl border border-slate-200 p-5">
          <p id={idPregunta} aria-live="polite" className="text-sm font-semibold text-slate-800">{textoDe(paso.pregunta, estado.current)}</p>
          <div className="mt-3 space-y-2">
            {opciones.map((o, i) => (
              <button
                key={o.valor}
                ref={i === 0 ? primeraOpcion : undefined}
                type="button"
                aria-label={o.etiqueta}
                aria-describedby={o.detalle ? `${idDetalle}-${i}` : undefined}
                onClick={() => responder(o)}
                className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-brand-400 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <span className="block text-sm font-medium text-slate-800">{o.etiqueta}</span>
                {o.detalle && <span id={`${idDetalle}-${i}`} className="block text-xs text-slate-500">{o.detalle}</span>}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">{pie}{botonSaltar}</div>
        </div>
      </div>,
      document.body,
    );
  }

  const ultimo = resolverPaso(guia, (pos ?? 0) + 1, estado.current) >= total;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const ancho = Math.min(ANCHO_MAX, vw - 2 * MARGEN);
  // Arriba del campo si entra (abajo suelen abrirse las listas de los buscadores); si no, abajo.
  const arriba = rect ? rect.top - SEPARACION - altoBurbuja : MARGEN;
  const top = rect
    ? (arriba >= MARGEN ? arriba : Math.max(MARGEN, Math.min(rect.top + rect.height + SEPARACION, vh - altoBurbuja - MARGEN)))
    : MARGEN;
  const left = rect ? Math.min(Math.max(rect.left, MARGEN), Math.max(MARGEN, vw - MARGEN - ancho)) : MARGEN;

  return createPortal(
    <>
      {rect && (
        <div
          aria-hidden="true"
          className="fixed z-[65] pointer-events-none rounded-lg ring-2 ring-brand-500 transition-all duration-150"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.2)' }}
        />
      )}
      <div
        ref={burbuja}
        role="dialog"
        aria-modal="false"
        aria-label={`Ayuda: ${guia.nombre}`}
        className="fixed z-[65] bg-white rounded-xl shadow-xl border border-slate-200 p-4"
        style={{ top, left, width: ancho }}
      >
        <p aria-live="polite" className="text-sm text-slate-700">{textoDe(paso.texto, estado.current)}</p>
        <div className="mt-3 flex items-center justify-end gap-2">
          {botonSaltar}
          <button
            type="button"
            onMouseDown={sinRobarFoco}
            onClick={siguiente}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-500 min-h-[2.5rem]"
          >
            {ultimo ? 'Listo' : 'Siguiente'}
          </button>
        </div>
        <div className="mt-2">{pie}</div>
      </div>
    </>,
    document.body,
  );
}
